import { Dialog, DialogContent, IconButton, Stack, Switch, Typography } from '@mui/material'
import { Close } from '@mui/icons-material'
import React, { Dispatch, SetStateAction, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNotification } from '../../store/redux/hooks'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import AutocompleteDefaultProject from './AutocompleteDefaultProject'
import { Option } from '../Form/Inputs/AutocompleteInput'
import DefaultWorkingHoursPickers from './DefaultWorkingHoursPickers'
import ConfirmButton from '../Button/ConfirmButton'
import { useChangeSettingsMutation, useGetCurrentUserQuery } from '../../store/redux/reducers/usersSlice'
import { createErrorMessage } from '../../store/utils'
import dayjs from 'dayjs'
import DateFormat from '../../store/types/enums/DateFormat'
import { setSettings } from '../../store/redux/reducers/settingsSlice'
import NumberInputHoursThreshold from './NumberInputHoursThreshold'
import AutocompleteFavoriteProjects from './AutocompleteFavoriteProjects'
import AutocompleteSuggestedProjects from './AutocompleteSuggestedProjects'
import { Project } from '../../store/types'
import { useChangeFavoriteProjectsMutation, useGetAcronymsQuery } from '../../store/redux/reducers/projectsSlice'
import { UUID } from 'crypto'
import AutocompleteNotificationType from './AutocompleteNotificationType'
import NotificationType from '../../store/types/enums/NotificationType'
import AutocompleteNotificationReason from './AutocompleteNotificationReason'
import { NotificationReason } from '../../store/types/enums/NotificationReason'
import PageSectionDivider from './PageSectionDivider'

type PageHeaderAvatarDialogProps = {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
}

const PageHeaderAvatarDialog = ({ open, setOpen }: PageHeaderAvatarDialogProps) => {
  const dispatch = useDispatch()
  const { displayNotification } = useNotification()
  const { t } = useTranslation()

  const [changeSettings] = useChangeSettingsMutation()
  const { data: currentUserWithSettings } = useGetCurrentUserQuery({})

  const currentUser = useMemo(() => currentUserWithSettings && currentUserWithSettings.user, [currentUserWithSettings])
  const settings = useMemo(() => currentUserWithSettings && currentUserWithSettings.settings, [currentUserWithSettings])

  const allReasonsOptions: NotificationReason[] = [
    NotificationReason.EMPLOYEE_VACATION_CREATED,
    NotificationReason.EMPLOYEE_UPCOMING_VACATION,
    NotificationReason.NEW_CANDIDATE_COMMENT,
  ]

  const allUnmutedReasons = allReasonsOptions.filter(reason => !settings?.mutedNotificationReasons.includes(reason))

  const defaultStartTime = settings?.defaultWorkingHours?.start || '08:00'
  const defaultEndTime = settings?.defaultWorkingHours?.end || '16:00'
  const defaultNotificationType = settings?.notificationType || NotificationType.PUSH

  const [startTime, setStartTime] = useState<string>(defaultStartTime)
  const [endTime, setEndTime] = useState<string>(defaultEndTime)
  const [defaultProject, setDefaultProject] = useState<Option | null>(settings?.defaultProject || null)
  const [favoriteProjects, setFavoriteProjects] = useState<Project[]>([])
  const [notificationType, setNotificationType] = useState<NotificationType>(defaultNotificationType)
  const [unmutedReasons, setUnmutedReasons] = useState<NotificationReason[]>(allUnmutedReasons)
  const [hideScheduleFlag, setHideScheduleFlag] = useState<boolean>(settings?.hideSchedule || false)
  const [remoteWorkModeFlag, setRemoteWorkModeFlag] = useState<boolean>(settings?.remoteWorkMode || false)
  const [workHoursSortByFavoriteFlag, setWorkHoursSortByFavoriteFlag] = useState<boolean>(
    settings?.workHoursSortByFavorite || false
  )
  const [numberHoursThreshold, setNumberHoursThreshold] = useState<string>(
    settings?.workHourCollapseThreshold.toString() || ''
  )
  const [loading, setLoading] = useState<boolean>(false)

  const { data: favoriteProjectsResponse, isSuccess } = useGetAcronymsQuery({
    page: 0,
    size: 100,
    favorite: true,
  })
  const dialogContentRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isSuccess) {
      setFavoriteProjects(
        favoriteProjectsResponse.content.map(projectAcronym => {
          return {
            id: projectAcronym.id,
            acronym: projectAcronym.acronym,
            name: projectAcronym.name,
            projectStatus: projectAcronym.projectStatus,
            favorite: projectAcronym.favorite,
          } as Project
        })
      )
    }
  }, [favoriteProjectsResponse, isSuccess])

  useLayoutEffect(() => {
    if (!open || !dialogContentRef.current) {
      return
    }
    const contentElement = dialogContentRef.current
    contentElement.scrollTop = 0
  }, [open])

  const isError = useMemo(() => {
    return dayjs(startTime, DateFormat.HourMinute).isAfter(dayjs(endTime, DateFormat.HourMinute))
  }, [endTime, startTime])

  const [changeFavoriteProjects] = useChangeFavoriteProjectsMutation()

  const handleChangeSettings = async () => {
    if (!currentUser?.id) {
      return
    }
    setLoading(true)
    try {
      const mutedNotificationReasonsToSave = allReasonsOptions.filter(reason => !unmutedReasons.includes(reason))

      await changeSettings({
        id: currentUser?.id,
        start: startTime,
        end: endTime,
        hideSchedule: hideScheduleFlag,
        remoteWorkMode: remoteWorkModeFlag,
        defaultProjectId: defaultProject?.value,
        workHourCollapseThreshold: parseInt(numberHoursThreshold),
        workHoursSortByFavorite: workHoursSortByFavoriteFlag,
        notificationType,
        mutedNotificationReasons: mutedNotificationReasonsToSave,
      }).unwrap()

      const favoriteIds: UUID[] = favoriteProjects.map(p => p.id as UUID)
      await changeFavoriteProjects(favoriteIds)

      dispatch(
        setSettings({
          hideSchedule: hideScheduleFlag,
          remoteWorkMode: remoteWorkModeFlag,
          defaultProject: defaultProject,
          defaultWorkingHours: { start: startTime, end: endTime },
          workHourCollapseThreshold: parseInt(numberHoursThreshold),
          workHoursSortByFavorite: workHoursSortByFavoriteFlag,
          notificationType,
          mutedNotificationReasons: mutedNotificationReasonsToSave,
        })
      )
      displayNotification({ message: t('message.userSettingsSaved'), type: 'success' })
      setOpen(false)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: errorDescription || t('message.somethingWentWrong'), type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleSaveButtonClick = () => {
    handleChangeSettings()
  }

  const handleCloseDialog = () => {
    setOpen(false)
    setStartTime(defaultStartTime)
    setEndTime(defaultEndTime)
    setDefaultProject(settings?.defaultProject || null)
    setHideScheduleFlag(settings?.hideSchedule || false)
    setRemoteWorkModeFlag(settings?.remoteWorkMode || false)
    setFavoriteProjects(
      favoriteProjectsResponse?.content.map(projectAcronym => {
        return {
          id: projectAcronym.id,
          acronym: projectAcronym.acronym,
          name: projectAcronym.name,
          projectStatus: projectAcronym.projectStatus,
          favorite: projectAcronym.favorite,
        } as Project
      }) || []
    )
  }

  const hideScheduleClick = () => {
    setHideScheduleFlag(!hideScheduleFlag)
  }

  const remoteWorkModeClick = () => {
    setRemoteWorkModeFlag(!remoteWorkModeFlag)
  }

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      fullWidth
      maxWidth={'sm'}
      sx={{ '.MuiDialog-paper': { p: 4, gap: 2 } }}
      aria-labelledby="custom-dialog-title"
    >
      <Stack direction={'row'} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography component={'h3'} variant={'h3'} color="text.highEmphasis" id="custom-dialog-title">
          {t('page.name.settings')}
        </Typography>
        <IconButton aria-label="close" onClick={handleCloseDialog}>
          <Close />
        </IconButton>
      </Stack>
      <DialogContent
        ref={dialogContentRef}
        sx={{
          px: 0,
          py: 2,
          overflowY: 'auto',
          scrollbarGutter: 'stable',
        }}
      >
        <Stack sx={{ gap: 4, pr: 1.5 }}>
          <PageSectionDivider title={t('components.settingsDialog.work')} />
          <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack>
              <Typography component={'h4'} variant={'h5'} color="text.highEmphasis" id="hide-schedule-label">
                {t('components.settingsDialog.hideSchedule')}
              </Typography>
              <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                {t('components.settingsDialog.hideScheduleDescription')}
              </Typography>
            </Stack>
            <Switch
              checked={hideScheduleFlag}
              onClick={hideScheduleClick}
              inputProps={{ 'aria-labelledby': 'hide-schedule-label' }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Stack>
              <Typography component={'h4'} variant={'h5'} color="text.highEmphasis" id="remote-work-mode-label">
                {t('components.settingsDialog.remoteWorkMode')}
              </Typography>
              <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                {t('components.settingsDialog.remoteWorkModeDescription')}
              </Typography>
            </Stack>
            <Switch
              checked={remoteWorkModeFlag}
              onClick={remoteWorkModeClick}
              inputProps={{ 'aria-labelledby': 'remote-work-mode-label' }}
            />
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
          >
            <Stack>
              <Typography component={'h4'} variant={'h5'} color="text.highEmphasis">
                {t('components.settingsDialog.defaultPlannedWorkingHours')}
              </Typography>
              <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                {t('components.settingsDialog.defaultPlannedWorkingHoursDescription')}
              </Typography>
            </Stack>
            <DefaultWorkingHoursPickers
              startTime={startTime}
              setStartTime={setStartTime}
              endTime={endTime}
              setEndTime={setEndTime}
              isError={isError}
            />
          </Stack>
          <PageSectionDivider title={t('components.settingsDialog.projects')} />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
          >
            <Stack width={{ xs: '100%', sm: '60%' }}>
              <Typography component={'h4'} variant={'h5'} color="text.highEmphasis">
                {t('components.settingsDialog.defaultProject')}
              </Typography>
              <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                {t('components.settingsDialog.defaultProjectDescription')}
              </Typography>
            </Stack>
            <AutocompleteDefaultProject
              defaultProject={defaultProject}
              defaultProjectChange={setDefaultProject}
              sxProps={{ width: { xs: '100%', sm: '60%' } }}
            />
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
          >
            <Stack width={{ xs: '100%', sm: '60%' }}>
              <Typography component={'h4'} variant={'h5'} color="text.highEmphasis">
                {t('components.settingsDialog.favoriteProjects')}
              </Typography>
              <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                {t('components.settingsDialog.favoriteProjectsDescription')}
              </Typography>
            </Stack>
            <AutocompleteFavoriteProjects
              favoriteProjects={favoriteProjects}
              favoriteProjectsChange={setFavoriteProjects}
              sxProps={{
                width: { xs: '100%', sm: '60%' },
              }}
            />
          </Stack>
          <PageSectionDivider title={t('components.settingsDialog.worklog')} />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
          >
            <Stack width={{ xs: '100%', sm: '60%' }}>
              <Typography component={'h4'} variant={'h5'} color="text.highEmphasis">
                {t('components.settingsDialog.suggestedProjects')}
              </Typography>
              <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                {t('components.settingsDialog.suggestedProjectsDescription')}
              </Typography>
            </Stack>
            <AutocompleteSuggestedProjects
              workHoursSortByFavoriteFlag={workHoursSortByFavoriteFlag}
              workHoursSortByFavoriteFlagChange={setWorkHoursSortByFavoriteFlag}
              sxProps={{ width: { xs: '100%', sm: '60%' } }}
            />
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
          >
            <Stack width={{ xs: '100%', sm: '60%' }}>
              <Typography component={'h4'} variant={'h5'} color="text.highEmphasis">
                {t('components.settingsDialog.workHourCollapseThreshold')}
              </Typography>
              <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                {t('components.settingsDialog.workHourCollapseThresholdDescription')}
              </Typography>
            </Stack>
            <NumberInputHoursThreshold
              value={numberHoursThreshold}
              setValue={setNumberHoursThreshold}
              sxProps={{ width: { xs: '100%', sm: '40%' } }}
            />
          </Stack>
          <PageSectionDivider title={t('components.settingsDialog.notifications')} />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
          >
            <Stack width={{ xs: '100%', sm: '60%' }}>
              <Typography component={'h4'} variant={'h5'} color="text.highEmphasis">
                {t('components.settingsDialog.notificationType')}
              </Typography>
              <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                {t('components.settingsDialog.notificationTypeDescription')}
              </Typography>
            </Stack>
            <AutocompleteNotificationType
              notificationType={notificationType}
              notificationTypeChange={setNotificationType}
              sxProps={{ width: { xs: '100%', sm: '60%' } }}
            />
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
          >
            <Stack width={{ xs: '100%', sm: '60%' }}>
              <Typography component={'h4'} variant={'h5'} color="text.highEmphasis">
                {t('components.settingsDialog.notificationReason')}
              </Typography>
              <Typography fontSize={'small'} fontStyle={'italic'} color="text.base">
                {t('components.settingsDialog.notificationReasonDescription')}
              </Typography>
            </Stack>
            <AutocompleteNotificationReason
              unmutedReasons={unmutedReasons}
              setUnmutedReasons={setUnmutedReasons}
              sxProps={{
                width: { xs: '100%', sm: '60%' },
                '& .MuiChip-root': { maxWidth: { xs: '100% !important', sm: '200px !important' } },
              }}
            />
          </Stack>
          <ConfirmButton
            text={t('page.usersProfile.header.save')}
            onClick={handleSaveButtonClick}
            isLoadingButton={true}
            isLoading={loading}
            isDisabled={loading || isError}
            autoFocus={false}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default PageHeaderAvatarDialog
